import type { RouteLocationNormalized } from 'vue-router'

import type { RoutesWithThreadIdParam } from './types'

import { ROUTE_COMMUNITY } from '~/contexts/community/router/routes'

import { Navigation404Error, NavigationRedirectError, NavigationToModalError } from '~/router/errors'
import { hasParamsChanged } from '~/router/utils/has-params-changed'

import { getCommunityWhenNotMembers } from '~/entities/container/community/api/communities'
import { ECommunityAccess } from '~/entities/container/community/interfaces/community'

import { isOnPremise } from '~/features-shared/config-manager/stores/swym-config'
import { useCommunityFacetStore } from '~/features-shared/facet/stores/facets'

import { verifyFeatureAvailability } from '~/features-generic/config-manager/composables/feature-manager'

import type { NavigationGuard } from '~/typings/vue-router'

/**
 * Check the given community exists
 * @param to the route location "to"
 */
async function checkCommunityExist(to: RouteLocationNormalized<RoutesWithThreadIdParam>): Promise<void> {
  const containerUri = to.params.thread_id
  try {
    await getCommunityWhenNotMembers(containerUri)
  }
  catch {
    throw new Navigation404Error('This community does not exist')
  }
}

/**
 * Check if the user can access this community
 * Open Request Modal is the community is private
 * @param to the route location "to"
 */
async function checkUserHasAccess(to: RouteLocationNormalized<RoutesWithThreadIdParam>): Promise<void> {
  const containerUri = to.params.thread_id
  const community = await getCommunityWhenNotMembers(containerUri)

  if (
    !community.hasUserAccess
    && community.access === ECommunityAccess.Private
    && !community.tou_to_sign
  ) {
    throw new NavigationToModalError(
      'User is not a member of this community',
      'modal-community-signup-request',
      { community },
    )
  }
}

/**
 * Check if the community is a personal space
 * @param to the route location "to"
 * @throws NavigationToModalError if the community is a personal space
 */
async function checkCommunityIsNotPersonalSpace(to: RouteLocationNormalized<RoutesWithThreadIdParam>): Promise<void> {
  const containerUri = to.params.thread_id
  const community = await getCommunityWhenNotMembers(containerUri)

  if (community?.community_type === 'personalspace')
    throw new NavigationRedirectError('Community is a personal space', '/me/personal-space')
}

/**
 * Check if the community has the terms of use to sign
 * @param to the route location "to"
 * @throws NavigationToModalError if the community has the terms of use to sign
 */
async function checkCommunityTermsOfUse(to: RouteLocationNormalized<RoutesWithThreadIdParam>): Promise<void> {
  const containerUri = to.params.thread_id
  const community = await getCommunityWhenNotMembers(containerUri)

  if (community.tou_to_sign && to.name !== ROUTE_COMMUNITY.URI_TERMS_OF_USE) {
    throw new NavigationToModalError(
      'Community has terms of use to sign',
      'modal-community-sign-terms-of-use',
      {
        communityUri: community.subject_uri,
        redirectTo: to,
      },
    )
  }
}

/**
 * Check if a facet experience exist in the community before going to it
 * @param to
 * @throws NavigationRedirectError if the facet experience does not exist
 * @throws NavigationRedirectError if the user does not have access to the facet experience
 */
async function checkCommunityExperienceFacetExist(to: RouteLocationNormalized<ROUTE_COMMUNITY.URI_FACETS_EXPERIENCE>) {
  const containerUri = to.params.thread_id
  const facetUri = to.params.facet_uri

  const isGAFacetFeatureActivated = await verifyFeatureAvailability('community.features.ga_facet')
  const isLAFacetFeatureActivated = await verifyFeatureAvailability('community.features.la_facet')
  const isFacetFeatureActivated = (isGAFacetFeatureActivated || isLAFacetFeatureActivated) && !isOnPremise()

  if (!isFacetFeatureActivated) {
    throw new NavigationRedirectError(
      'User does not have access to experience facet feature',
      { name: ROUTE_COMMUNITY.URI_HOME, params: { ...to.params } },
    )
  }

  const { fetchOrGetCommunityFacet } = useCommunityFacetStore()
  const facet = await fetchOrGetCommunityFacet(containerUri, facetUri)

  if (!facet) {
    throw new NavigationRedirectError(
      'This facet does not exist in this community',
      { name: ROUTE_COMMUNITY.URI_HOME, params: { ...to.params } },
    )
  }
}

/**
 * Control access to the community context
 */
export const navigationGuardCommunity: NavigationGuard<ROUTE_COMMUNITY> = (to, from, next) => {
  let promise = Promise.resolve()

  if ('thread_id' in to.params) {
    const _to = to as RouteLocationNormalized<RoutesWithThreadIdParam>
    if (hasParamsChanged(_to, from, 'thread_id')) {
      promise = promise.then(() => checkCommunityExist(_to))
        .then(() => checkUserHasAccess(_to))
        .then(() => checkCommunityIsNotPersonalSpace(_to))
        .then(() => checkCommunityTermsOfUse(_to))
    }
    if (hasParamsChanged(to, from, 'facet_uri')) {
      const _to = to as RouteLocationNormalized<ROUTE_COMMUNITY.URI_FACETS_EXPERIENCE>
      promise = promise.then(() => checkCommunityExperienceFacetExist(_to))
    }
  }

  promise = promise
    .then(() => next())
    .catch((error) => {
      if (error instanceof NavigationRedirectError)
        return next(error.to)

      next(error)
    })
  return promise
}
